import React from 'react';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { AppliedOnType } from 'talent_matching/types';
import {
  type RediscoveryFormValues,
  REDISCOVERY_FORM_FIELD_KEYS,
  type RediscoveryPageDefaults,
} from 'talent_matching/talent_rediscovery/types';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import AppliedFilterChip, {
  type AppliedFilterChipProps,
} from 'talent_matching/components/applied_filters/applied_filter_chip';
import AppliedAdvancedFilterChip from 'talent_matching/components/applied_filters/applied_advanced_filter_chip';
import BaseAppliedFiltersRow from 'talent_matching/components/shared/base_applied_filters_row';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { formatShortDate } from 'talent_search/lib/utils';
import {
  APPLIED_ON_TEXT_MAPPING,
  isAppliedOnFiltered,
} from 'talent_matching/applied_on';

const t = withLocalePrefix('talent_matching.filters.applied');

type Props = {
  formDefaults: RediscoveryPageDefaults;
};

function itemLabels(items: Item[]): string {
  return items.map((i) => i.label).join(', ');
}

function buildFilters(
  values: RediscoveryFormValues,
  userDateFormat: string
): Omit<AppliedFilterChipProps, 'formDefaults'>[] {
  const filters: Omit<AppliedFilterChipProps, 'formDefaults'>[] = [];

  if (values.can_have?.length > 0) {
    filters.push({
      label: t('keywords.can_have', { keywords: values.can_have.join(', ') }),
      filterKeys: ['can_have'],
      uniqueKey: 'keywords.can_have',
    });
  }

  if (values.must_have?.length > 0) {
    filters.push({
      label: t('keywords.must_have', { keywords: values.must_have.join(', ') }),
      filterKeys: ['must_have'],
      uniqueKey: 'keywords.must_have',
    });
  }

  if (values.does_not_have?.length > 0) {
    filters.push({
      label: t('keywords.does_not_have', {
        keywords: values.does_not_have.join(', '),
      }),
      filterKeys: ['does_not_have'],
      uniqueKey: 'keywords.does_not_have',
    });
  }

  if (values.milestone) {
    filters.push({
      label: t('milestone', { milestones: values.milestone.label }),
      filterKeys: ['milestone'],
      uniqueKey: 'milestone',
    });
  }

  if (values.stages?.length) {
    filters.push({
      label: t('stages', { stages: itemLabels(values.stages) }),
      filterKeys: ['stages'],
      uniqueKey: 'stages',
    });
  }

  if (values.include_rejection_reasons?.length > 0) {
    filters.push({
      label: t('include_rejection_reasons', {
        reasons: itemLabels(values.include_rejection_reasons),
      }),
      filterKeys: ['include_rejection_reasons'],
      uniqueKey: 'include_rejection_reasons',
    });
  }

  if (values.exclude_rejection_reasons?.length > 0) {
    filters.push({
      label: t('exclude_rejection_reasons', {
        reasons: itemLabels(values.exclude_rejection_reasons),
      }),
      filterKeys: ['exclude_rejection_reasons'],
      uniqueKey: 'exclude_rejection_reasons',
    });
  }

  if (values.scorecard_performance) {
    filters.push({
      label: t('scorecard_performance', {
        performance: values.scorecard_performance.label,
      }),
      filterKeys: ['scorecard_performance'],
      uniqueKey: 'scorecard_performance',
    });
  }

  if (values.scorecard_attribute_filters?.length > 0) {
    const matchType = values.scorecard_attribute_match ?? 'any';
    const key =
      matchType === 'all'
        ? 'scorecard_attributes_all'
        : 'scorecard_attributes_any';
    filters.push({
      label: t(key, {
        attributes: itemLabels(values.scorecard_attribute_filters),
      }),
      filterKeys: ['scorecard_attribute_filters'],
      uniqueKey: 'scorecard_attribute_filters',
    });
  }

  if (values.no_strong_no_scores) {
    filters.push({
      label: t('no_strong_no_scores'),
      filterKeys: ['no_strong_no_scores'],
      uniqueKey: 'no_strong_no_scores',
    });
  }

  if (values.exclude_hired) {
    filters.push({
      label: t('exclude_hired'),
      filterKeys: ['exclude_hired'],
      uniqueKey: 'exclude_hired',
    });
  }

  if (values.exclude_rejected) {
    filters.push({
      label: t('exclude_rejected'),
      filterKeys: ['exclude_rejected'],
      uniqueKey: 'exclude_rejected',
    });
  }

  if (values.exclude_current_job_people) {
    filters.push({
      label: t('exclude_current_job_people'),
      filterKeys: ['exclude_current_job_people'],
      uniqueKey: 'exclude_current_job_people',
    });
  }

  if (values.without_candidate_tags?.length > 0) {
    filters.push({
      label: t('without_candidate_tags', {
        tags: itemLabels(values.without_candidate_tags),
      }),
      filterKeys: ['without_candidate_tags'],
      uniqueKey: 'without_candidate_tags',
    });
  }

  if (
    isAppliedOnFiltered({
      appliedOnType: values.appliedOnType,
      appliedOnStart: values.appliedOnStart,
      appliedOnEnd: values.appliedOnEnd,
    })
  ) {
    const startDateText =
      formatShortDate(values.appliedOnStart, userDateFormat) || '...';
    const endDateText =
      formatShortDate(values.appliedOnEnd, userDateFormat) || '...';
    const appliedOnText = APPLIED_ON_TEXT_MAPPING[
      values.appliedOnType as AppliedOnType
    ]?.(startDateText, endDateText);

    if (appliedOnText) {
      filters.push({
        label: t('applied_on', { when: appliedOnText }),
        filterKeys: ['appliedOnStart', 'appliedOnEnd', 'appliedOnType'],
        uniqueKey: 'appliedOn',
      });
    }
  }

  if (values.jobs?.length > 0) {
    filters.push({
      label: t('jobs', { jobs: itemLabels(values.jobs) }),
      filterKeys: ['jobs'],
      uniqueKey: 'jobs',
    });
  }

  if (values.departments?.length > 0) {
    filters.push({
      label: t('departments', { departments: itemLabels(values.departments) }),
      filterKeys: ['departments'],
      uniqueKey: 'departments',
    });
  }

  if (values.offices?.length > 0) {
    filters.push({
      label: t('offices', { offices: itemLabels(values.offices) }),
      filterKeys: ['offices'],
      uniqueKey: 'offices',
    });
  }

  return filters;
}

const RediscoveryAppliedFilters = ({ formDefaults }: Props) => {
  const { control } = useFormContext();
  const { userDateFormat } = useUserContext();
  const watchedValues = useWatch({
    control,
    name: REDISCOVERY_FORM_FIELD_KEYS,
  });

  const values: RediscoveryFormValues = Object.fromEntries(
    REDISCOVERY_FORM_FIELD_KEYS.map((key, i) => [key, watchedValues[i]])
  ) as RediscoveryFormValues;

  const appliedAdvancedFilters = (values.advancedFilters ?? []).filter(
    (f: AdvancedFilter) => f.queryKey?.() && f.queryParamValue?.()
  );

  const chips: React.ReactNode[] = [
    ...buildFilters(values, userDateFormat).map((filter) => (
      <AppliedFilterChip
        key={filter.uniqueKey}
        formDefaults={formDefaults}
        {...filter}
      />
    )),
    ...appliedAdvancedFilters.map((filter: AdvancedFilter) => (
      <AppliedAdvancedFilterChip
        filter={filter}
        key={filter.reactKey}
        fieldName="advancedFilters"
      />
    )),
  ];

  return <BaseAppliedFiltersRow chips={chips} />;
};

export default RediscoveryAppliedFilters;
